import { ICellRendererParams } from '@ag-grid-community/core'
import { generateMdiIcon, SELECT_ALL } from '@helios/ag-grid-config'
import { ReconRunStatus } from './recon-run-status.model'

interface IssueDataSourceMap {
  custody?: number
  trading?: number
  no_source?: number
  both?: number
  total?: number
}

export interface IssueCounts {
  found?: {
    high?: IssueDataSourceMap
    medium?: IssueDataSourceMap
  }
  waiting?: {
    high?: IssueDataSourceMap
    medium?: IssueDataSourceMap
  }
}

export interface Account {
  accountType: 'simple' | 'aggregate' | 'sleeveSimple' | 'sleeveAggregate'
  availableActions?: AccountAvailableActions[]
  accountUsers?: string[]
  accountId: number
  accountName: string
  accountBasis: string[]
  startDate?: string
  custodyAccountNumber: string
  custodyDataFeeds?: string
  tradingDataFeeds?: string
  clientId: number
  clientName: string
  custodyBankName?: string
  sleeveAggregateId?: number
  opsManager: string
  reconciler?: string
  secTypes?: string
  manager: string
  priority: PriorityType
  nextLockdown?: string
  lastPerseusRunEtlState?: boolean
  lastPerseusRun?: string
  lastPerseusRunReconDate?: string
  lastPerseusRunOmegaAchieved?: boolean
  isStable: boolean
  etlLockedDate?: string
  isReconciling: boolean
  isMirrored: boolean
  isTriparty: boolean
  isAutomaticEtlRun: boolean
  isDemo: boolean
  isLIB: boolean
  bloombergDataEnabled?: boolean
  externalReferenceSource?: string
  libStartDate?: string
  traderCodes?: string
  omegaDate?: string
  hasOmegaDateToday?: boolean
  contractualDeadline?: string
  preferredDeadline?: string
  secondaryRep?: string
  complianceViolations?: number
  positionIssues?: IssueCounts
  otherIssues?: IssueCounts
  corporateActionsIssues?: IssueCounts
  amortizationIssues?: IssueCounts
  distributionIssues?: IssueCounts
  lotEntryAndExitIssues?: IssueCounts
  priceAndAnalyticsIssues?: IssueCounts
  securityCreationIssues?: IssueCounts
  transactionBreaksIssues?: IssueCounts
  transactionDataIntegrityIssues?: IssueCounts
  custodyReconciled?: string
  hasCustodyTimestamp?: boolean
  tradingReconciled?: string
  externalCustody?: string
  externalTrading?: string
  hasComplianceTimestamp: boolean
  hasPricingRun: boolean
  perseusRunStatus?: ReconRunStatus
  parentClientId?: number
  parentClientName?: string
  implementationCompleteDate?: string

  highPriorityTotal?: number
  mediumPriorityTotal?: number
  heldPriorityTotal?: number
  clientVisibleFoundTotal?: number
  clientVisibleHeldTotal?: number

  reconTimestamp?: string
  maxTradingReconciled?: string
  maxCustodyReconciled?: string
  latestCustodyExecutionActive?: boolean
  latestTradingExecutionActive?: boolean
  elmIssues?: IssueCounts
}

export type AggregateAccount = Pick<Account, 'accountType'>

export type AccountAvailableActions = 'run' | 'runWithoutEtls'
export type PriorityType = 'high' | 'medium' | 'low' | 'unknown'
export const FILTER_ROW_COUNT = 10000

export interface AccountsRequestContext {
  filterContext: AdvancedFilterContext
}

export interface AdvancedFilterContext {
  /**
   * The recon date filter does nothing on its own. It provides context for other filters.
   * Currently this gives context to filters: hasOmegaDate, hasReconTimestamp & hasTradingReconTimestamp
   */
  reconDate: Date | string
  advancedFilters: AdvancedFilters
}

export interface AdvancedFilters {
  aggregateId?: number[]
  hasOmegaDate?: boolean
  hasWaitingIssues?: boolean
  hasFoundIssues?: boolean
  hasTradingReconTimestamp?: boolean
  isReconciled?: boolean
  isTradingReconciled?: boolean
  parentClientId?: number[]
  security?: SecurityFilter
  quickSearch?: QuickSearchFilter
  includeNonReconciling?: boolean
}
export interface SecurityFilter {
  securityIdentifier: string
  currentlyHeld: boolean
}

export interface QuickSearchFilter {
  substring: string
  substrings?: string[]
}

export interface AccountsSetValues {
  accountTypes: string[]
  clients: string[]
  parentClients: string[]
  opsManagers: string[]
  reconcilers: string[]
  managers: string[]
  priorities: string[]
  contractualDeadlines: string[]
  preferredDeadlines: string[]
  secondaryReps: string[]
  perseusRunStatus: ReconRunStatus[]
  sleeveAggregateIds: string[]
  accountBases: string[]
  securityTypes: string[]
  externalReferenceSources?: string[]
}

export interface AccountListAggregate {
  accountId: number
  shortName: string
}

export const accountTypes = new Map([
  [
    'simple',
    {
      icon: generateMdiIcon('mdi-file-outline'),
      toolTip: 'Simple Account',
      displayName: 'Simple',
    },
  ],
  [
    'aggregate',
    {
      icon: generateMdiIcon('mdi-file-multiple-outline'),
      toolTip: 'Aggregate Account',
      displayName: 'Aggregate',
    },
  ],
  [
    'sleeveSimple',
    {
      icon: generateMdiIcon('mdi-file'),
      toolTip: 'Sub-Sleeve',
      displayName: 'Sub-Sleeve',
    },
  ],
  [
    'sleeveAggregate',
    {
      icon: generateMdiIcon('mdi-file-multiple'),
      toolTip: 'Parent Portfolio',
      displayName: 'Parent Portfolio',
    },
  ],
])

export const accountTypesFilterCellRenderer = (params: ICellRendererParams) => {
  if (params.value === SELECT_ALL) {
    return SELECT_ALL
  }

  const icon = accountTypes.get(params.value)?.icon ?? ''
  const text = accountTypes.get(params.value)?.displayName ?? ''

  return `${icon} ${text}`
}

export const isAccountSleeveType = (account: Account) => {
  return ['sleeveSimple', 'sleeveAggregate'].includes(account.accountType)
}
